import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportScope } from '@/api/support/supportApi.types';
import { uploadAttachmentWithProgress } from '@/components/chat/ChatComposer/uploadAttachmentWithProgress';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import {
  useInitSupportMessageAttachmentMutation,
  useSendSupportMessageMutation,
} from '@/hooks/useSupport';
import {
  SUPPORT_MEDIA_ACCEPT_ATTRIBUTE,
  SUPPORT_MEDIA_ALLOWED_CONTENT_TYPES,
} from '@/constants/support';
import { isAcceptedContentType } from '@/constants/media';
import { showErrorNotification } from '@/utils/notifications';

function generateTemporaryMessageId(): string {
  return crypto.randomUUID();
}

interface PendingAttachment {
  file: File;
  attachmentId: string | null;
  status: 'uploading' | 'ready' | 'failed';
  progressPercent: number;
}

interface SupportComposerProps {
  scope: SupportScope;
  ticketId: string;
  ownSenderKind: string;
  isSendEnabled: boolean;
  disabledNotice: string;
}

export function SupportComposer({
  scope,
  ticketId,
  ownSenderKind,
  isSendEnabled,
  disabledNotice,
}: SupportComposerProps) {
  const { t } = useTranslation('support');
  const [text, setText] = useState('');
  const [pendingAttachment, setPendingAttachment] = useState<PendingAttachment | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initUploadMutation = useInitSupportMessageAttachmentMutation(scope, ticketId);
  const sendMessageMutation = useSendSupportMessageMutation(scope, ticketId, ownSenderKind);

  async function handleFileSelected(selectedFile: File) {
    if (isAcceptedContentType(selectedFile, SUPPORT_MEDIA_ALLOWED_CONTENT_TYPES) === false) {
      showErrorNotification(t('support:errors.uploadFailed'));
      return;
    }
    setPendingAttachment({
      file: selectedFile,
      attachmentId: null,
      status: 'uploading',
      progressPercent: 0,
    });
    try {
      const initResult = await initUploadMutation.mutateAsync({
        filename: selectedFile.name,
        contentType: selectedFile.type,
        sizeBytes: selectedFile.size,
      });
      await uploadAttachmentWithProgress(
        initResult.uploadUrl,
        selectedFile,
        initResult.contentType,
        function reportUploadProgress(progressPercent) {
          setPendingAttachment(function applyProgress(currentAttachment) {
            if (currentAttachment === null) {
              return currentAttachment;
            }
            return { ...currentAttachment, progressPercent };
          });
        }
      );
      setPendingAttachment({
        file: selectedFile,
        attachmentId: initResult.attachmentId,
        status: 'ready',
        progressPercent: 100,
      });
    } catch {
      setPendingAttachment({
        file: selectedFile,
        attachmentId: null,
        status: 'failed',
        progressPercent: 0,
      });
      showErrorNotification(t('support:errors.uploadFailed'));
    }
  }

  function removePendingAttachment() {
    setPendingAttachment(null);
    if (fileInputRef.current !== null) {
      fileInputRef.current.value = '';
    }
  }

  function submitMessage() {
    const trimmedText = text.trim();
    const attachmentIds =
      pendingAttachment !== null &&
      pendingAttachment.status === 'ready' &&
      pendingAttachment.attachmentId !== null
        ? [pendingAttachment.attachmentId]
        : [];
    if (trimmedText.length === 0 && attachmentIds.length === 0) {
      return;
    }
    sendMessageMutation.mutate({
      tempId: generateTemporaryMessageId(),
      body: trimmedText.length > 0 ? trimmedText : null,
      attachmentIds,
    });
    setText('');
    removePendingAttachment();
  }

  if (isSendEnabled === false) {
    return (
      <div className="border-t border-zooborder bg-zoooffwhite px-4 py-4 text-center text-[13px] text-zoomuted italic">
        {disabledNotice}
      </div>
    );
  }

  return (
    <div className="border-t border-zooborder bg-zoowhite px-4 py-3">
      {pendingAttachment === null ? null : (
        <div className="mb-2 px-3 py-2 rounded-xl bg-zoovpalelavender text-sm text-zooindigo">
          <div className="flex items-center gap-2">
            <Icon name="Paperclip" className="shrink-0" />
            <span className="flex-1 truncate">{pendingAttachment.file.name}</span>
            {pendingAttachment.status === 'uploading' ? (
              <span className="shrink-0 text-xs font-semibold text-zooviolet">
                {pendingAttachment.progressPercent}%
              </span>
            ) : null}
            {pendingAttachment.status === 'failed' ? (
              <span className="text-zooerrortext font-semibold">
                {t('support:errors.uploadFailed')}
              </span>
            ) : null}
            <button
              type="button"
              onClick={removePendingAttachment}
              aria-label={t('support:composer.removeAttachment')}
              className="text-zoomuted hover:text-zooerrortext"
            >
              <Icon name="X" />
            </button>
          </div>
          {pendingAttachment.status === 'uploading' ? (
            <div
              role="progressbar"
              aria-label={t('support:composer.uploadProgressLabel')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={pendingAttachment.progressPercent}
              className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-zoowhite"
            >
              <div
                className="h-full rounded-full bg-zooprimary transition-all"
                style={{ width: pendingAttachment.progressPercent + '%' }}
              />
            </div>
          ) : null}
        </div>
      )}
      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept={SUPPORT_MEDIA_ACCEPT_ATTRIBUTE}
          className="hidden"
          onChange={function onFilePicked(changeEvent) {
            const selectedFile =
              changeEvent.target.files === null ? null : changeEvent.target.files[0];
            if (selectedFile !== undefined && selectedFile !== null) {
              handleFileSelected(selectedFile);
            }
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('support:composer.attach')}
          onClick={function openFilePicker() {
            if (fileInputRef.current !== null) {
              fileInputRef.current.click();
            }
          }}
        >
          <Icon name="Paperclip" />
        </Button>
        <textarea
          value={text}
          onChange={function updateText(changeEvent) {
            setText(changeEvent.target.value);
          }}
          onKeyDown={function submitOnEnter(keyboardEvent) {
            if (keyboardEvent.key === 'Enter' && keyboardEvent.shiftKey === false) {
              keyboardEvent.preventDefault();
              submitMessage();
            }
          }}
          placeholder={t('support:composer.placeholder')}
          rows={1}
          className="flex-1 resize-none px-4 py-2.5 bg-zoooffwhite border border-zooborder rounded-xl text-sm text-zooindigo transition-all focus:outline-none focus:border-zooprimary max-h-32"
        />
        <Button
          type="button"
          size="icon"
          aria-label={t('support:composer.send')}
          onClick={submitMessage}
        >
          <Icon name="Send" />
        </Button>
      </div>
    </div>
  );
}
